'use client';

import * as React from 'react';

import type { ToastActionElement, ToastProps } from '@/components/ui/Toast';

const TOAST_LIMIT = 3;

type ToastVariant = 'default' | 'success' | 'info' | 'warning' | 'error';

type ToastT = {
  id: string;
  title?: string;
  description?: string;
  action?: ToastActionElement;
  variant?: ToastVariant;
  duration?: number;
};

const variantDurations: Record<ToastVariant, number> = {
  success: 3000,
  info: 4000,
  warning: 5000,
  error: 7000,
  default: 4000,
};

type Action =
  | { type: 'ADD_TOAST'; toast: ToastT }
  | { type: 'UPDATE_TOAST'; toast: Partial<ToastT> }
  | { type: 'DISMISS_TOAST'; toastId?: string }
  | { type: 'REMOVE_TOAST'; toastId?: string };

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) return;

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({ type: 'REMOVE_TOAST', toastId });
  }, 5000);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: ToastT[], action: Action): ToastT[] => {
  switch (action.type) {
    case 'ADD_TOAST':
      return [action.toast, ...state].slice(0, TOAST_LIMIT);

    case 'UPDATE_TOAST':
      return state.map((t) =>
        t.id === action.toast.id ? { ...t, ...action.toast } : t
      );

    case 'DISMISS_TOAST': {
      const { toastId } = action;
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.forEach((t) => addToRemoveQueue(t.id));
      }
      return state;
    }

    case 'REMOVE_TOAST':
      if (action.toastId === undefined) return [];
      return state.filter((t) => t.id !== action.toastId);

    default:
      return state;
  }
};

const listeners: Array<(state: ToastT[]) => void> = [];
let memoryState: ToastT[] = [];

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => listener(memoryState));
}

type ToastOptions = Omit<ToastT, 'id'>;

function toast(opts: ToastOptions) {
  const id = genId();
  const variant = opts.variant ?? 'default';
  const duration = opts.duration ?? variantDurations[variant];

  dispatch({
    type: 'ADD_TOAST',
    toast: { ...opts, id, variant, duration },
  });

  return {
    id,
    dismiss: () => dispatch({ type: 'DISMISS_TOAST', toastId: id }),
    update: (props: Partial<ToastT>) =>
      dispatch({ type: 'UPDATE_TOAST', toast: { ...props, id } }),
  };
}

function useToast() {
  const [state, setState] = React.useState<ToastT[]>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) listeners.splice(index, 1);
    };
  }, []);

  return {
    toasts: state,
    toast,
    dismiss: (toastId?: string) =>
      dispatch({ type: 'DISMISS_TOAST', toastId }),
  };
}

export { useToast, toast };
